/**
 * Data for the product screens.
 *
 * The business id is in the query KEY, not merely an argument inside `queryFn` —
 * the same rule `useParties` follows, and for the same reason: a key that omits
 * the company will happily serve the previous company's catalogue after a switch.
 */
import { useQuery } from '@tanstack/react-query';
import { getDb } from '@data/db/database';
import {
  listCategories,
  listProducts,
  listTaxRates,
  type ListProductsOptions,
} from '@data/services/productReader';
import { useActiveBusiness } from '@features/documents/useDocuments';

export const PRODUCTS_QUERY_ROOT = ['products'] as const;
export const PRODUCT_FILTERS_QUERY_ROOT = ['product-filters'] as const;

export function useProducts(options: ListProductsOptions = {}) {
  const { kind, activeOnly = false, search = '', categoryId, limit } = options;
  const business = useActiveBusiness();
  const businessId = business.data ?? null;

  return useQuery({
    queryKey: [
      ...PRODUCTS_QUERY_ROOT,
      'list',
      businessId,
      kind ?? 'ALL',
      activeOnly,
      search,
      categoryId ?? null,
      limit ?? null,
    ],
    queryFn: async () => {
      if (businessId === null) return [];
      return listProducts(getDb() as never, businessId, { kind, activeOnly, search, categoryId, limit });
    },
    enabled: businessId !== null,
  });
}

/**
 * Categories and tax rates for the filter controls.
 *
 * Separate key from the product list, because they change far less often — a
 * category rename must not invalidate 500 rows, and a 60-second staleTime keeps
 * the dropdown stable while the user types in the search box.
 */
export function useProductFilters() {
  const business = useActiveBusiness();
  const businessId = business.data ?? null;

  return useQuery({
    queryKey: [...PRODUCT_FILTERS_QUERY_ROOT, businessId],
    queryFn: async () => {
      if (businessId === null) return { categories: [], taxRates: [] };
      const db = getDb() as never;
      const [categories, taxRates] = await Promise.all([
        listCategories(db, businessId),
        listTaxRates(db, businessId),
      ]);
      return { categories, taxRates };
    },
    enabled: businessId !== null,
  });
}
